import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Header from '../components/layout/Header';
import { Trophy, Crown, ExternalLink, Share2, Twitter, Facebook, Copy, X, Download, Gamepad2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Winners = () => {
  const navigate = useNavigate();
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedWinner, setSelectedWinner] = useState(null);

  useEffect(() => {
    const fetchWinners = async () => {
      try {
        const response = await axios.get(`${API}/winners`);
        setWinners(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Failed to fetch winners:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchWinners();
  }, []);

  // Group winners by challenge - sorted by latest first
  const groupedWinners = useMemo(() => {
    const groups = {};
    const safeWinners = Array.isArray(winners) ? winners : [];
    safeWinners.forEach(winner => {
      const challengeId = winner.challenge_id || 'unknown';
      if (!groups[challengeId]) {
        groups[challengeId] = {
          challenge_id: challengeId,
          challenge_title: winner.challenge_title || 'Challenge',
          challenge_start_time: winner.challenge_start_time,
          challenge_end_time: winner.challenge_end_time,
          game_name: winner.game_name,
          game_cover_url: winner.game_cover_url,
          winners: [],
          latestDate: null
        };
      }
      groups[challengeId].winners.push(winner);
      // Track the latest announced_at date for sorting challenges
      const winnerDate = winner.announced_at ? new Date(winner.announced_at) : null;
      if (winnerDate && (!groups[challengeId].latestDate || winnerDate > groups[challengeId].latestDate)) {
        groups[challengeId].latestDate = winnerDate;
      }
    });
    // Sort winners within each group by place
    Object.values(groups).forEach(group => {
      group.winners.sort((a, b) => (a.place || 1) - (b.place || 1));
    });
    // Sort challenge groups by latest first
    return Object.values(groups).sort((a, b) => {
      if (!a.latestDate && !b.latestDate) return 0;
      if (!a.latestDate) return 1;
      if (!b.latestDate) return -1;
      return b.latestDate - a.latestDate;
    });
  }, [winners]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const openShareModal = (winner) => {
    setSelectedWinner(winner);
    setShowShareModal(true);
  };

  const getPlaceBadge = (place) => {
    switch(place) {
      case 1: return { emoji: '🥇', text: '1st', color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/50', gradient: 'from-yellow-500/20 to-yellow-600/10' };
      case 2: return { emoji: '🥈', text: '2nd', color: 'text-gray-300', bg: 'bg-gray-500/20', border: 'border-gray-500/50', gradient: 'from-gray-500/20 to-gray-600/10' };
      case 3: return { emoji: '🥉', text: '3rd', color: 'text-amber-500', bg: 'bg-amber-500/20', border: 'border-amber-500/50', gradient: 'from-amber-500/20 to-amber-600/10' };
      default: return { emoji: '🥇', text: '1st', color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/50', gradient: 'from-yellow-500/20 to-yellow-600/10' };
    }
  };

  const getPrizeDisplay = (winner) => {
    if (winner.prize_info?.prize_type === 'money' || !winner.prize_info) {
      return <span className="text-green-400 font-bold">${winner.prize_info?.amount || winner.prize_amount || 0}</span>;
    } else if (winner.prize_info?.prize_type === 'game_code') {
      return <span className="text-blue-400 font-bold">🎮 Game Code</span>;
    } else if (winner.prize_info?.prize_type === 'item') {
      return <span className="text-purple-400 font-bold">🎁 Prize Item</span>;
    }
    return <span className="text-green-400 font-bold">${winner.prize_amount || 0}</span>;
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />

      <main className="pt-20 sm:pt-24 pb-12 px-3 sm:px-4 max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-yellow-400 mb-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <Crown className="inline-block w-8 sm:w-10 h-8 sm:h-10 mr-2" />
            Hall of Winners
          </h1>
          <p className="text-gray-400 text-sm sm:text-base">All past challenge winners and their achievements</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full"></div>
          </div>
        ) : winners.length === 0 ? (
          <Card className="glass-card p-8 sm:p-12 text-center border-red-900/30">
            <Crown className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2 text-gray-400">No Winners Yet</h3>
            <p className="text-gray-500 mb-6">Be the first to win a challenge!</p>
            <Button className="bg-red-600 hover:bg-red-700" onClick={() => navigate('/')}>
              Enter Challenge
            </Button>
          </Card>
        ) : (
          /* Challenge cards grid - bigger cards */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {groupedWinners.map((group) => (
              <Card key={group.challenge_id} className="glass-card overflow-hidden border-purple-500/30">
                {/* Challenge Header */}
                <div className="p-4 sm:p-5 border-b border-purple-500/20 bg-gradient-to-r from-purple-500/10 via-transparent to-blue-500/10">
                  <div className="flex items-center gap-4">
                    {group.game_cover_url && (
                      <img 
                        src={group.game_cover_url} 
                        alt={group.game_name}
                        className="w-14 h-20 object-cover rounded-lg border border-purple-500/30 shadow-lg"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg sm:text-xl font-bold text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                        {group.challenge_title}
                      </h2>
                      {group.game_name && (
                        <div className="flex items-center gap-2 mt-1">
                          <Gamepad2 className="w-4 h-4 text-purple-400" />
                          <span className="text-sm text-purple-400">{group.game_name}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-xs text-gray-500">
                          {group.winners.length} winner{group.winners.length > 1 ? 's' : ''}
                        </span>
                        {(group.challenge_start_time || group.challenge_end_time) && (
                          <span className="text-xs text-gray-600">
                            📅 {formatDate(group.challenge_start_time) || '?'} - {formatDate(group.challenge_end_time) || '?'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Winners List */}
                <div className="p-4 space-y-3">
                  {group.winners.map((winner) => {
                    const placeBadge = getPlaceBadge(winner.place);
                    
                    return (
                      <div 
                        key={winner.id} 
                        className={`relative p-4 rounded-xl bg-gradient-to-r ${placeBadge.gradient} border ${placeBadge.border} hover:bg-black/40 transition-all overflow-visible`}
                      >
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                          {/* Place Badge and Profile */}
                          <div className="flex items-center gap-3">
                            {/* Place Badge - Inline */}
                            <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full ${placeBadge.bg} ${placeBadge.color} flex flex-col items-center justify-center border-2 ${placeBadge.border} shadow-lg`}>
                              <span className="text-base sm:text-lg">{placeBadge.emoji}</span>
                              <span className="text-[8px] sm:text-[10px] font-black">{placeBadge.text}</span>
                            </div>
                          
                            {/* Profile Image */}
                            <img 
                              src={winner.profile_image_url || 'https://via.placeholder.com/56'} 
                              alt={winner.display_name}
                              className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 ${placeBadge.border} shadow-lg`}
                            />
                          
                            {/* Winner Info - visible on mobile inline */}
                            <div className="min-w-0 sm:hidden">
                              <div className={`text-base font-bold ${placeBadge.color} truncate`}>
                                {winner.display_name}
                              </div>
                              <div className="text-xs text-gray-500">@{winner.twitch_username}</div>
                              <div className="text-sm mt-0.5">
                                {getPrizeDisplay(winner)}
                              </div>
                            </div>
                          </div>
                          
                          {/* Winner Info - desktop */}
                          <div className="hidden sm:block flex-1 min-w-0">
                            <div className={`text-lg font-bold ${placeBadge.color}`}>
                              {winner.display_name}
                            </div>
                            <div className="text-sm text-gray-500">@{winner.twitch_username}</div>
                            <div className="text-base mt-1">
                              {getPrizeDisplay(winner)}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex sm:flex-col gap-2 w-full sm:w-auto">
                            <a 
                              href={winner.video_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm transition-all flex-1 sm:flex-none"
                            >
                              <ExternalLink className="w-4 h-4" />
                              Watch
                            </a>
                            <button
                              onClick={() => openShareModal(winner)}
                              className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 text-sm transition-all flex-1 sm:flex-none"
                              data-testid={`share-winner-${winner.id}`}
                            >
                              <Share2 className="w-4 h-4" />
                              Share
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <footer className="py-8 px-4 border-t border-red-900/20">
        <div className="max-w-7xl mx-auto text-center text-gray-600">
          <p className="flex items-center justify-center gap-2">
            <Trophy className="w-4 h-4 text-red-500" />
            CesarM Challenges
          </p>
        </div>
      </footer>

      {showShareModal && selectedWinner && (
        <ShareModal
          winner={selectedWinner}
          onClose={() => {
            setShowShareModal(false);
            setSelectedWinner(null);
          }}
        />
      )}
    </div>
  );
};

// Share Modal - Direct Image URL for Twitter
const ShareModal = ({ winner, onClose }) => {
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [sharePageUrl, setSharePageUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const placeBadge = {
    1: { emoji: '🥇', text: '1ST PLACE', gradient: 'from-yellow-500 to-amber-600' },
    2: { emoji: '🥈', text: '2ND PLACE', gradient: 'from-gray-400 to-gray-500' },
    3: { emoji: '🥉', text: '3RD PLACE', gradient: 'from-amber-600 to-amber-700' }
  }[winner.place || 1];

  const getPrizeText = () => {
    if (winner.prize_info?.prize_type === 'money' || !winner.prize_info) {
      return `$${winner.prize_info?.amount || winner.prize_amount || 0}`;
    } else if (winner.prize_info?.prize_type === 'game_code') {
      return winner.prize_info?.description || 'Game Code Prize';
    } else if (winner.prize_info?.prize_type === 'item') {
      return winner.prize_info?.description || 'Prize Item';
    }
    return 'Winner';
  };

  // Generate image and upload to get direct image URL
  const generateImage = async () => {
    if (imageUrl) return { imageUrl, sharePageUrl };
    
    setGenerating(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#0a0a0a',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      
      const imageData = canvas.toDataURL('image/jpeg', 0.92);
      
      // Upload to backend
      const response = await axios.post(`${API}/share-card/upload`, {
        image_data: imageData,
        winner_id: winner.id,
        winner_name: winner.display_name,
        challenge_title: winner.challenge_title || 'Challenge',
        place: winner.place || 1,
        prize: getPrizeText()
      });
      
      const shareId = response.data.share_id;
      const directImageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share-card/${shareId}/image`;
      // Use backend URL for Twitter sharing (has server-side meta tags)
      const twitterShareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/${shareId}`;
      
      setImageUrl(directImageUrl);
      setSharePageUrl(twitterShareUrl);
      
      return { imageUrl: directImageUrl, sharePageUrl: twitterShareUrl };
    } catch (error) {
      console.error('Failed to generate image:', error);
      toast.error('Failed to create share image');
      return null;
    } finally {
      setGenerating(false);
    }
  };

  // Share to Twitter - uses backend share page with proper Twitter Card meta tags
  const shareToTwitter = async () => {
    const result = await generateImage();
    if (!result) return;
    
    // Just share the URL - Twitter will pick up the image from meta tags
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(result.sharePageUrl)}`, '_blank');
    toast.success('Opening Twitter - image will show in preview!');
  };

  // Share to Facebook
  const shareToFacebook = async () => {
    const result = await generateImage();
    if (!result) return;
    
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(result.sharePageUrl)}`, '_blank');
    toast.success('Opening Facebook...');
  };

  // Copy direct image URL
  const copyImageUrl = async () => {
    const result = await generateImage();
    if (!result) return;
    
    try {
      await navigator.clipboard.writeText(result.imageUrl);
      setCopied(true);
      toast.success('Image URL copied! Paste anywhere to show the image.');
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = result.imageUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      toast.success('Image URL copied!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  // Copy share page URL
  const copyShareUrl = async () => {
    const result = await generateImage();
    if (!result) return;
    
    try {
      await navigator.clipboard.writeText(result.sharePageUrl);
      toast.success('Share link copied!');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Download card
  const downloadCard = async () => {
    setGenerating(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#0a0a0a',
        scale: 2,
        useCORS: true,
        allowTaint: true
      });
      
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `winner_${winner.display_name}_${Date.now()}.jpg`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('Card downloaded!');
      }, 'image/jpeg', 0.92);
    } catch (error) {
      toast.error('Failed to download');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-2xl bg-gradient-to-br from-gray-900 via-gray-900 to-purple-950/30 border border-purple-500/30 rounded-2xl shadow-2xl overflow-hidden max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-purple-500/20 bg-gradient-to-r from-purple-500/10 to-transparent sticky top-0 bg-gray-900/95 backdrop-blur z-10">
          <div className="flex items-center gap-3">
            <Share2 className="w-5 h-5 text-purple-400" />
            <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              Share Winner
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Visual Share Card - Twitter Summary Large Image (2:1 ratio) */}
          <div 
            ref={cardRef}
            style={{
              width: '600px',
              height: '315px',
              background: 'linear-gradient(145deg, #0f0f1a 0%, #1a1a2e 40%, #16213e 70%, #0f0f23 100%)',
              borderRadius: '16px',
              overflow: 'hidden',
              position: 'relative',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
          >
            {/* Background Glow Effects */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '200px',
              height: '200px',
              background: 'radial-gradient(circle, rgba(234,179,8,0.2) 0%, transparent 70%)',
              pointerEvents: 'none'
            }} />
            
            {/* Main Content */}
            <div style={{ position: 'relative', padding: '24px', height: '100%' }}>
              
              {/* Row 1: Challenge + Badge */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                
                {/* Left: Game Cover + Challenge Info */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  {winner.game_cover_url && (
                    <img 
                      src={winner.game_cover_url} 
                      alt=""
                      crossOrigin="anonymous"
                      style={{
                        width: '50px',
                        height: '68px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '2px solid rgba(147,51,234,0.5)',
                        flexShrink: 0
                      }}
                    />
                  )}
                  <div>
                    <div style={{ fontSize: '9px', color: '#a78bfa', letterSpacing: '2px', fontWeight: 700, marginBottom: '2px' }}>CHALLENGE</div>
                    <div style={{ fontSize: '16px', fontWeight: 800, color: 'white', lineHeight: 1.2 }}>
                      {winner.challenge_title || 'Gaming Challenge'}
                    </div>
                    {winner.game_name && (
                      <div style={{ fontSize: '12px', color: '#a78bfa', marginTop: '4px' }}>
                        🎮 {winner.game_name}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Right: Place Badge */}
                <div style={{
                  background: winner.place === 1 ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 
                             winner.place === 2 ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)' :
                             'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                  padding: '10px 20px',
                  borderRadius: '25px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                  flexShrink: 0
                }}>
                  <span style={{ fontSize: '20px' }}>{placeBadge.emoji}</span>
                  <span style={{ fontSize: '14px', fontWeight: 900, color: 'white', letterSpacing: '1px' }}>{placeBadge.text}</span>
                </div>
              </div>
              
              {/* Row 2: Winner Info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                
                {/* Profile Picture Container */}
                <div style={{ position: 'relative', width: '80px', height: '80px', flexShrink: 0 }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    border: '3px solid #eab308',
                    overflow: 'hidden',
                    boxShadow: '0 0 20px rgba(234,179,8,0.3)',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {winner.profile_image_url ? (
                      <img 
                        src={winner.profile_image_url} 
                        alt=""
                        crossOrigin="anonymous"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <span style={{ fontSize: '32px', fontWeight: 900, color: 'white' }}>
                        {winner.display_name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    )}
                  </div>
                  {/* Crown Badge */}
                  <div style={{
                    position: 'absolute',
                    bottom: '0px',
                    right: '0px',
                    width: '28px',
                    height: '28px',
                    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid #0f0f1a'
                  }}>
                    <span style={{ fontSize: '14px' }}>👑</span>
                  </div>
                </div>
                
                {/* Name + Handle */}
                <div>
                  <div style={{ fontSize: '24px', fontWeight: 900, color: 'white', marginBottom: '2px' }}>
                    {winner.display_name}
                  </div>
                  <div style={{ fontSize: '14px', color: '#a78bfa' }}>@{winner.twitch_username}</div>
                </div>
              </div>

              {/* Row 3: Prize + Branding */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                
                {/* Prize Box */}
                <div style={{
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(34,197,94,0.4)',
                  borderRadius: '12px',
                  padding: '10px 16px'
                }}>
                  <div style={{ fontSize: '9px', color: '#9ca3af', letterSpacing: '1px', fontWeight: 700, marginBottom: '2px' }}>💰 PRIZE WON</div>
                  <div style={{ fontSize: '22px', fontWeight: 900, color: '#4ade80' }}>
                    {getPrizeText()}
                  </div>
                </div>
                
                {/* Branding */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>
                    {winner.announced_at ? new Date(winner.announced_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                  <div style={{ fontSize: '13px', color: '#a78bfa', fontWeight: 700 }}>
                    🏆 CesarM Challenges
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Generated URLs */}
          {imageUrl && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 space-y-3">
              <p className="text-xs text-green-400 text-center font-bold">✅ Image ready to share!</p>
              
              {/* Direct Image URL */}
              <div>
                <label className="text-[10px] text-gray-400 uppercase tracking-wider">Direct Image URL (for Twitter)</label>
                <div className="flex items-center gap-2 bg-black/40 rounded-lg p-2 mt-1">
                  <input 
                    type="text" 
                    value={imageUrl} 
                    readOnly 
                    className="flex-1 bg-transparent text-xs text-gray-300 outline-none truncate"
                  />
                  <button 
                    onClick={copyImageUrl}
                    className={`px-3 py-1 rounded text-xs font-bold transition-all ${copied ? 'bg-green-500 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'}`}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Share Page URL */}
              <div>
                <label className="text-[10px] text-gray-400 uppercase tracking-wider">Share Page URL</label>
                <div className="flex items-center gap-2 bg-black/40 rounded-lg p-2 mt-1">
                  <input 
                    type="text" 
                    value={sharePageUrl} 
                    readOnly 
                    className="flex-1 bg-transparent text-xs text-gray-300 outline-none truncate"
                  />
                  <button 
                    onClick={copyShareUrl}
                    className="px-3 py-1 rounded text-xs font-bold bg-gray-700 hover:bg-gray-600 text-gray-300 transition-all"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Main Action: Share to Twitter */}
          <Button
            className="w-full h-14 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 font-bold text-lg"
            onClick={shareToTwitter}
            disabled={generating}
            data-testid="share-twitter-btn"
          >
            {generating ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                Generating image...
              </>
            ) : (
              <>
                <Twitter className="w-5 h-5 mr-2" />
                Share to Twitter
              </>
            )}
          </Button>
          
          <p className="text-xs text-gray-500 text-center">
            Image will automatically appear in your tweet preview!
          </p>

          {/* Other Options */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={shareToFacebook}
              disabled={generating}
              className="flex items-center justify-center gap-2 p-4 rounded-xl bg-black/30 border border-gray-700 hover:border-blue-600 hover:bg-blue-600/10 transition-all disabled:opacity-50"
            >
              {generating ? (
                <div className="w-5 h-5 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
              ) : (
                <Facebook className="w-5 h-5 text-blue-600" />
              )}
              <span className="text-sm text-white">Facebook</span>
            </button>

            <button
              onClick={copyImageUrl}
              disabled={generating}
              className="flex items-center justify-center gap-2 p-4 rounded-xl bg-black/30 border border-gray-700 hover:border-green-500 hover:bg-green-500/10 transition-all disabled:opacity-50"
            >
              {generating ? (
                <div className="w-5 h-5 border-2 border-green-500/30 border-t-green-500 rounded-full animate-spin" />
              ) : (
                <Copy className="w-5 h-5 text-green-500" />
              )}
              <span className="text-sm text-white">Copy Image URL</span>
            </button>
          </div>

          {/* Download Button */}
          <button
            onClick={downloadCard}
            disabled={generating}
            className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-black/30 border border-gray-700 hover:border-purple-500 hover:bg-purple-500/10 transition-all disabled:opacity-50"
          >
            {generating ? (
              <div className="w-4 h-4 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
            ) : (
              <Download className="w-4 h-4 text-purple-500" />
            )}
            <span className="text-sm text-gray-300">Download Card</span>
          </button>

          <Button variant="outline" className="w-full border-gray-700 hover:bg-gray-800" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Winners;
